import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Check, BookOpen, Crown, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";

const PremiumSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const { user, refreshPremium } = useAuth();
  
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const activatePremium = async () => {
      // Get user from storage if not in context
      let userId = user?.id;
      if (!userId) {
        try {
          const { data } = await supabase.auth.getSession();
          userId = data?.session?.user?.id;
        } catch (e) {
          console.error('Error getting user:', e);
        }
      }

      if (!userId) {
        // User not logged in, but payment was made
        // We'll activate based on session_id later via webhook
        setStatus("success");
        toast.success("Paiement reçu ! Connectez-vous pour activer Premium.");
        return;
      }

      try {
        // Insert or update premium status. user_id is UNIQUE, so onConflict
        // ensures we update existing rows instead of failing on duplicates.
        const { error } = await supabase
          .from('premium_users')
          .upsert(
            {
              user_id: userId,
              method: 'stripe',
              activated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' }
          );

        if (error) {
          console.error('Error activating premium:', error);
        }

        // Refresh premium status
        if (refreshPremium) {
          await refreshPremium();
        }

        setStatus("success");
        toast.success("Bienvenue parmi les Premium !");
      } catch (error) {
        console.error("Error activating premium:", error);
        setStatus("success"); // Still show success - webhook will handle it
        toast.success("Paiement reçu !");
      }
    };

    // Small delay to ensure everything is loaded
    setTimeout(activatePremium, 1000);
  }, [sessionId, user, refreshPremium]);

  if (status === "processing") {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-gold-leaf animate-spin mx-auto mb-6" />
          <h1 className="font-heading text-2xl text-ink-primary mb-2">
            Activation en cours...
          </h1>
          <p className="font-body text-ink-secondary">
            Veuillez patienter quelques instants.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="text-center max-w-lg animate-fade-in">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-8">
          <Check className="h-10 w-10 text-green-600" strokeWidth={2} />
        </div>
        
        <h1 className="font-heading text-4xl text-ink-primary mb-4">
          Merci pour votre confiance !
        </h1>
        
        <div className="flex items-center justify-center gap-2 mb-6">
          <Crown className="h-5 w-5 text-gold-leaf" />
          <span className="font-body text-lg text-ink-secondary">
            Vous êtes maintenant <strong className="text-gold-leaf">Premium</strong>
          </span>
        </div>
        
        <p className="font-body text-ink-secondary mb-8">
          Vous avez maintenant accès à toutes les fonctionnalités Premium.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={() => navigate("/bible/genese/1")}
            className="bg-ink-primary hover:bg-ink-secondary text-paper"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Commencer la lecture
          </Button>
          
          <Button 
            onClick={() => navigate("/")}
            variant="outline"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PremiumSuccessPage;
